import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import {
  Box,
  createSvgIcon,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { ChartsTooltipContainer } from '@mui/x-charts';
import { PieChart as MuiPieChart } from '@mui/x-charts/PieChart';
import type { ReactNode } from 'react';
import { GlossaryTooltip } from '../../components/GlossaryTooltip';
import { ReportMetricValueSkeleton } from '../../components/ReportMetricValueSkeleton';
import { getPercentage } from '../../helpers';
import { useReportsRole } from '../../ReportsConfiguration/useReportsRole';

function PieChartTooltip({
  buySide,
  sellSide,
}: {
  buySide: number;
  sellSide: number;
}) {
  const { isAgent } = useReportsRole();
  const total = buySide + sellSide;

  const buyPercentage = getPercentage(buySide, total);
  const sellPercentage = getPercentage(sellSide, total);

  return (
    <ChartsTooltipContainer trigger="item">
      <Stack
        sx={{
          backgroundColor: 'common.white',
          borderRadius: '2px',
          padding: 1,
          border: '1px solid',
          borderColor: 'grey.500',
          gap: 1.25,
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: 'warning.500',
              flexShrink: 0,
            }}
          />
          <Typography variant="body3" sx={{ color: 'action.active' }}>
            Sell Side
          </Typography>
          <Typography
            variant="h6"
            component="span"
            sx={{ color: 'action.active' }}
          >
            {sellSide}
            <Typography
              variant="body3"
              sx={{ color: 'action.active', pl: 0.5 }}
            >
              ({sellPercentage}%)
            </Typography>
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: 'periwinkle.main',
              flexShrink: 0,
            }}
          />
          <Typography variant="body3" sx={{ color: 'action.active' }}>
            {isAgent ? 'Buy Side' : 'List Side'}
          </Typography>
          <Typography
            variant="h6"
            component="span"
            sx={{ color: 'action.active' }}
          >
            {buySide}
            <Typography
              variant="body3"
              sx={{ color: 'action.active', pl: 0.5 }}
            >
              ({buyPercentage}%)
            </Typography>
          </Typography>
        </Stack>
      </Stack>
    </ChartsTooltipContainer>
  );
}

export const TransactionsGoalPieChart = ({
  buySides,
  sellSides,
}: {
  buySides: number;
  sellSides: number;
}) => {
  const theme = useTheme();

  return (
    <MuiPieChart
      series={[
        {
          data: [
            {
              value: sellSides,
              color: theme.palette.warning[500],
              id: 'sellSide',
              label: 'Sell Side',
            },
            {
              value: buySides,
              color: theme.palette.periwinkle.main,
              id: 'buySide',
              label: 'Buy Side',
            },
          ],
          startAngle: -90,
          endAngle: 90,
          innerRadius: '60%',
          outerRadius: '100%',
          arcLabel: () => '',
        },
      ]}
      slots={{
        tooltip: () => (
          <PieChartTooltip buySide={buySides} sellSide={sellSides} />
        ),
      }}
      sx={{
        overflow: 'hidden',
        mx: -2,
        mb: -6.5,
        mt: -1,
        justifyContent: 'flex-end',
        pr: 3,
      }}
      width={90}
      height={90}
      hideLegend
    />
  );
};

const TargetIcon = createSvgIcon(
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Target</title>
    <path
      d="M6.75033 17.6769C5.73644 17.2394 4.85449 16.6457 4.10449 15.8957C3.35449 15.1457 2.76074 14.2637 2.32324 13.2498C1.88574 12.2359 1.66699 11.1526 1.66699 9.99984C1.66699 8.84706 1.88574 7.76373 2.32324 6.74984C2.76074 5.73595 3.35449 4.854 4.10449 4.104C4.85449 3.354 5.73644 2.76025 6.75033 2.32275C7.76421 1.88525 8.84755 1.6665 10.0003 1.6665C11.1531 1.6665 12.2364 1.88525 13.2503 2.32275C14.2642 2.76025 15.1462 3.354 15.8962 4.104C16.6462 4.854 17.2399 5.73595 17.6774 6.74984C18.1149 7.76373 18.3337 8.84706 18.3337 9.99984C18.3337 11.1526 18.1149 12.2359 17.6774 13.2498C17.2399 14.2637 16.6462 15.1457 15.8962 15.8957C15.1462 16.6457 14.2642 17.2394 13.2503 17.6769C12.2364 18.1144 11.1531 18.3332 10.0003 18.3332C8.84755 18.3332 7.76421 18.1144 6.75033 17.6769ZM14.7295 14.729C16.0212 13.4373 16.667 11.8609 16.667 9.99984C16.667 8.13873 16.0212 6.56234 14.7295 5.27067C13.4378 3.979 11.8614 3.33317 10.0003 3.33317C8.13921 3.33317 6.56283 3.979 5.27116 5.27067C3.97949 6.56234 3.33366 8.13873 3.33366 9.99984C3.33366 11.8609 3.97949 13.4373 5.27116 14.729C6.56283 16.0207 8.13921 16.6665 10.0003 16.6665C11.8614 16.6665 13.4378 16.0207 14.7295 14.729ZM6.45866 13.5415C5.48644 12.5693 5.00033 11.3887 5.00033 9.99984C5.00033 8.61095 5.48644 7.43039 6.45866 6.45817C7.43088 5.48595 8.61144 4.99984 10.0003 4.99984C11.3892 4.99984 12.5698 5.48595 13.542 6.45817C14.5142 7.43039 15.0003 8.61095 15.0003 9.99984C15.0003 11.3887 14.5142 12.5693 13.542 13.5415C12.5698 14.5137 11.3892 14.9998 10.0003 14.9998C8.61144 14.9998 7.43088 14.5137 6.45866 13.5415ZM12.3545 12.354C13.0073 11.7012 13.3337 10.9165 13.3337 9.99984C13.3337 9.08317 13.0073 8.29845 12.3545 7.64567C11.7017 6.99289 10.917 6.6665 10.0003 6.6665C9.08366 6.6665 8.29894 6.99289 7.64616 7.64567C6.99338 8.29845 6.66699 9.08317 6.66699 9.99984C6.66699 10.9165 6.99338 11.7012 7.64616 12.354C8.29894 13.0068 9.08366 13.3332 10.0003 13.3332C10.917 13.3332 11.7017 13.0068 12.3545 12.354ZM8.82324 11.1769C8.49685 10.8505 8.33366 10.4582 8.33366 9.99984C8.33366 9.5415 8.49685 9.14914 8.82324 8.82275C9.14963 8.49636 9.54199 8.33317 10.0003 8.33317C10.4587 8.33317 10.851 8.49636 11.1774 8.82275C11.5038 9.14914 11.667 9.5415 11.667 9.99984C11.667 10.4582 11.5038 10.8505 11.1774 11.1769C10.851 11.5033 10.4587 11.6665 10.0003 11.6665C9.54199 11.6665 9.14963 11.5033 8.82324 11.1769Z"
      fill="#606881"
    />
  </svg>,
  'TargetIcon',
);

interface MetricCardProps {
  value?: string;
  label: string;
  isLoading: boolean;
  bottomContent?: ReactNode;
  rightContent?: ReactNode;
  sx?: SxProps<Theme>;
}

export const MetricCard = ({
  value,
  label,
  isLoading,
  bottomContent,
  rightContent,
  sx,
}: MetricCardProps) => {
  return (
    <Box
      sx={{
        backgroundColor: 'grey.100',
        borderRadius: 1,
        p: 2,
        width: '100%',
        minHeight: { md: 130 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        ...sx,
      }}
    >
      <Stack direction="row">
        <Stack sx={{ gap: 0.5 }}>
          {isLoading ? (
            <ReportMetricValueSkeleton />
          ) : (
            <Typography variant="h3" component="span">
              {value ?? '––'}
            </Typography>
          )}
          <GlossaryTooltip label={label}>
            <Typography
              variant="body2"
              noWrap
              sx={{ minWidth: 0, color: 'text.secondary' }}
            >
              {label}
            </Typography>
          </GlossaryTooltip>
        </Stack>
        {rightContent}
      </Stack>
      {bottomContent && <Box sx={{ mt: 1 }}>{bottomContent}</Box>}
    </Box>
  );
};

export const GoalSubText = ({
  goal,
  actual,
}: {
  goal?: number | null;
  actual?: number | null;
}) => {
  if (!goal || actual === null || actual === undefined) {
    return undefined;
  }

  const percentage = getPercentage(actual, goal);

  if (actual > goal) {
    return (
      <Typography
        variant="body2"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: 'text.secondary',
        }}
      >
        <ArrowUpward sx={{ fontSize: 16 }} /> {percentage}% above goal
      </Typography>
    );
  }

  if (actual < goal) {
    return (
      <Typography
        variant="body2"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: 'text.secondary',
        }}
      >
        <ArrowDownward sx={{ fontSize: 16 }} /> {percentage}% below goal
      </Typography>
    );
  }

  return (
    <Typography
      variant="body2"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        color: 'text.secondary',
      }}
    >
      <TargetIcon sx={{ fontSize: 16 }} /> at goal
    </Typography>
  );
};
